import { useState, useMemo, useEffect } from 'react';
import { List, useListContext, CreateButton, TopToolbar, useRedirect } from 'react-admin';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { tbl, btn, inputSx, clickableA11y } from '../styles/ds';
import { useFitCount } from '../hooks/useFitCount';
import FitPagination from '../components/FitPagination';

const Toolbar = () => (
  <TopToolbar><CreateButton label="ספק חדש" sx={btn.create} /></TopToolbar>
);

const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('he-IL') : '—';

const SupplierTable = () => {
  const { data = [], isLoading } = useListContext();
  const redirect = useRedirect();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { fitCount, renderCount, containerRef, startIndex } = useFitCount({ fallback: 10, page });

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.trim().toLowerCase();
    return data.filter(s =>
      (s.name || '').toLowerCase().includes(q) ||
      (s.phone || '').includes(q) ||
      (s.email || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  useEffect(() => { setPage(1); }, [search]);

  const pageItems = useMemo(() => {
    return filtered.slice(startIndex, startIndex + renderCount);
  }, [filtered, startIndex, renderCount]);

  if (isLoading) return null;
  return (
    <Box>
      <Box className="crm-list-search" px={1} py={1.5}>
        <TextField size="small" placeholder="חיפוש ספק..." aria-label="חיפוש ספקים"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment>, sx: { ...inputSx, direction: 'rtl' } }}
          sx={{ width: 280 }} />
      </Box>
      <TableContainer component={Box} sx={{ ...tbl.container, border: '1px solid rgba(13,29,62,0.06)' }}>
        <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table>
          <TableHead><TableRow>
            <TableCell sx={{ width: 60 }}>#</TableCell>
            <TableCell>שם</TableCell>
            <TableCell>טלפון</TableCell>
            <TableCell>אימייל</TableCell>
            <TableCell>הערות</TableCell>
            <TableCell align="center">תאריך</TableCell>
          </TableRow></TableHead>
          <TableBody ref={containerRef}>
            {pageItems.map((s, i) => (
              <TableRow key={s.id} onClick={() => redirect('show', 'suppliers', s.id)}
                {...clickableA11y(() => redirect('show', 'suppliers', s.id), { ariaLabel: `פתיחת ספק ${s.name || ''}` })}
                sx={tbl.bodyRow(i)}>
                <TableCell><Typography fontWeight={600} color="text.secondary" fontSize={13}>#{s.id}</Typography></TableCell>
                <TableCell><Typography fontWeight={600} fontSize={14}>{s.name}</Typography></TableCell>
                <TableCell><Typography fontSize={13} color="text.secondary">{s.phone || '—'}</Typography></TableCell>
                <TableCell><Typography fontSize={13} color="text.secondary">{s.email || '—'}</Typography></TableCell>
                <TableCell><Typography fontSize={12} color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.notes || '—'}</Typography></TableCell>
                <TableCell align="center"><Typography fontSize={13} color="text.secondary">{fmtDate(s.created_at)}</Typography></TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>{search ? 'לא נמצאו תוצאות' : 'אין ספקים עדיין'}</TableCell></TableRow>
            )}
          </TableBody>
        </Table></Box>
      </TableContainer>
      <FitPagination page={page} setPage={setPage} total={filtered.length} perPage={fitCount} />
    </Box>
  );
};

const SupplierList = () => (
  <List className="crm-mobile-list crm-inline-list" sort={{ field: 'id', order: 'DESC' }} perPage={1000} pagination={false} storeKey={false} actions={<Toolbar />} title="ספקים" sx={tbl.listSx}>
    <SupplierTable />
  </List>
);
export default SupplierList;
